'use client';

import ImageDetailModal from '@/components/ui/ImageDetailModal/ImageDetailModal';
import ModalBase from '@/components/ui/ModalBase/ModalBase';
import RoundArrowIcon from '@/components/ui/RoundArrowIcon/RoundArrowIcon';
import { PRODUCT_OPTION_FLOW, PRODUCT_TYPE } from '@/constants';
import type { SizeSelectFrom } from '@/features/gfUserDesign/constants';
import { useGlobalLoadingAtom } from '@/state';
import utilStyles from '@/styles/modules/utils.module.scss';
import type { UserDesignItem } from '@/types';
import { sendClickUserDesignCreateGoodsEvent } from '@/utils/ga';
import type { Route } from 'next';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import DesignCtaButton from '../DesignCtaButton/DesignCtaButton';
import UserDesignDetailSlider, {
  type UserDesignDetailSliderRef,
} from '../UserDesignDetailSlider/UserDesignDetailSlider';
import styles from './UserDesignDetailModal.module.scss';

type UserDesignDetailModalProps = {
  isOpen: boolean;
  onClose: VoidFunction;
  items: UserDesignItem[];
  initialIndex: number;
  /** サイズ選択へ遷移するときの戻り先URL（呼び出し側が指定） */
  getCallbackUrl: (item: UserDesignItem) => Route;
  /** サイズ選択へ遷移するときの遷移元（パンくず用）。SIZE_SELECT_FROM で指定 */
  from: SizeSelectFrom;
};

function UserDesignDetailModal({
  isOpen,
  onClose,
  items,
  initialIndex,
  getCallbackUrl,
  from,
}: UserDesignDetailModalProps) {
  const router = useRouter();
  const [_, startGlobalLoading] = useGlobalLoadingAtom();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const sliderRef = useRef<UserDesignDetailSliderRef>(null);

  // モーダルが開かれたとき、またはinitialIndexが変更されたときにcurrentIndexを更新
  useEffect(() => {
    if (isOpen && initialIndex >= 0 && initialIndex < items.length) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex, items.length]);

  //有効なデータが存在するかのフラグ
  const isValid = items.length > 0 && initialIndex >= 0 && initialIndex < items.length;
  const isFirstSlide = currentIndex === 0;
  const isLastSlide = currentIndex === items.length - 1;

  // ドラッグ中は背景クリックでモーダルを閉じないようにする
  const shouldCloseOnBackgroundClick = !isDragging;

  const handlePreviewImageClick = useCallback((imageUrl: string) => {
    setSelectedImageUrl(imageUrl);
    setIsImageModalOpen(true);
  }, []);

  const handleCloseImageModal = useCallback(() => {
    setIsImageModalOpen(false);
    setSelectedImageUrl('');
  }, []);

  const handleGoDesignOptionSelect = useCallback(() => {
    if (!isValid || items[currentIndex] === undefined) return;
    const currentItem = items[currentIndex];
    if (!currentItem?.userPrintId) return;

    // GAイベント送信
    const materialIds = currentItem.materials.map((material) => material.id).join(',');
    sendClickUserDesignCreateGoodsEvent(
      currentItem.userPrintId,
      currentItem.gfWorkId,
      currentItem.gfWorkName,
      currentItem.productId,
      currentItem.productName,
      currentItem.productColorName,
      currentItem.productSizeName,
      materialIds,
    );

    // テンプレ商品はサイズ概念がなく size-select を持たないため、
    // コマ選択時と同じオプション選択画面（/product/{id}）へ遷移する。
    if (currentItem.productType === PRODUCT_TYPE.template) {
      // テンプレ商品は配置するユーザー素材（コマ）が 1 件のみ（postFromTemplate も単数の materialId を受け取る）
      const materialId = currentItem.materials[0]?.id;
      if (materialId === undefined) return;
      startGlobalLoading();
      const query = new URLSearchParams({
        materialId: String(materialId),
        workId: String(currentItem.gfWorkId),
        source: PRODUCT_OPTION_FLOW.existingDesign,
      }).toString();
      router.push(`/product/${currentItem.productId}?${query}`);
      return;
    }

    startGlobalLoading();

    const callbackUrl = getCallbackUrl(currentItem);
    const searchParams = new URLSearchParams({ callbackUrl, from });
    router.push(`/user-designs/${currentItem.userPrintId}/size-select?${searchParams.toString()}`);
  }, [isValid, currentIndex, items, getCallbackUrl, from, router, startGlobalLoading]);

  const renderCtaButton = () => (
    <DesignCtaButton
      onClick={handleGoDesignOptionSelect}
      text='このデザインからグッズをつくる'
      size='fullWidthMedium'
      disabled={isTransitioning}
    />
  );

  return (
    <>
      <ModalBase
        isOpen={isOpen}
        onClose={onClose}
        modalContainerSize='large'
        showDefaultCloseButton={true}
        showBottomCloseButton={false}
        closeOnBackgroundClick={shouldCloseOnBackgroundClick}
        bgColor='gray200'
        closeButtonBgColor='gray100'
      >
        {isValid && (
          <div className={styles.componentStyle}>
            <>
              <div className={styles.sliderArea}>
                <div className={styles.sliderWrapper}>
                  <UserDesignDetailSlider
                    ref={sliderRef}
                    items={items}
                    initialIndex={initialIndex}
                    onIndexChange={setCurrentIndex}
                    onTransitionChange={setIsTransitioning}
                    onDraggingChange={setIsDragging}
                    onPreviewImageClick={handlePreviewImageClick}
                    /** NOTE:SP版ではプレビューイメージの下にボタンを表示するため、アクションボタンの要素をスライダーに渡す */
                    childrenSpActionButton={<div className={utilStyles.onlySp}>{renderCtaButton()}</div>}
                  />
                </div>
                {/* PC版のアクションボタンを表示 */}
                <div className={`${styles.ctaButtonSection} ${utilStyles.onlyPc}`}>{renderCtaButton()}</div>
              </div>
              <div className={`${styles.navSection} ${utilStyles.onlyPc}`}>
                <button
                  type='button'
                  className={styles.navButton}
                  onClick={() => sliderRef.current?.goPrev()}
                  disabled={isFirstSlide}
                  aria-label='前のデザイン'
                >
                  <RoundArrowIcon direction='left' color='black' size='large' sizePc='large' bgColor='white' isBold />
                </button>
                <button
                  type='button'
                  className={styles.navButton}
                  onClick={() => sliderRef.current?.goNext()}
                  disabled={isLastSlide}
                  aria-label='次のデザイン'
                >
                  <RoundArrowIcon direction='right' color='black' size='large' sizePc='large' bgColor='white' isBold />
                </button>
              </div>
            </>
          </div>
        )}
      </ModalBase>
      <ImageDetailModal
        isOpen={isImageModalOpen}
        onClose={handleCloseImageModal}
        imageUrl={selectedImageUrl || null}
        alt='デザイン拡大プレビュー'
      />
    </>
  );
}
export default UserDesignDetailModal;
